
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { AppRouter } from "./AppRouter";
import "./index.css";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { initGA } from "./lib/analytics";

// Handle chunk loading failures (stale deployments) by auto-reloading
window.addEventListener('error', (event) => {
  if (
    event.message?.includes('Failed to fetch dynamically imported module') ||
    event.message?.includes('error loading dynamically imported module') ||
    event.message?.includes('Loading chunk') ||
    event.message?.includes('Loading CSS chunk') ||
    event.message?.includes('is not a valid JavaScript MIME type') ||
    event.message?.includes('text/html')
  ) {
    // Only reload once to prevent infinite loops
    const hasReloaded = sessionStorage.getItem('chunk-reload-attempted');
    if (!hasReloaded) {
      sessionStorage.setItem('chunk-reload-attempted', 'true');
      window.location.reload();
    }
  }
});

// Clear the reload flag on successful load
window.addEventListener('load', () => {
  sessionStorage.removeItem('chunk-reload-attempted');
});

// Create a new query client with refetchOnWindowFocus: false
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Disable automatic refetching when window regains focus
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

// /app/* is the phone app. It ships inside the website build, so the host's
// normal SPA fallback (every unknown path -> index.html) lands deep links here
// and publishing the site updates the installed app on its next open.
const isPhoneApp = window.location.pathname === '/app' || window.location.pathname.startsWith('/app/');

if (isPhoneApp) {
  document.title = 'RecruitLook Events';
  import('./mobile/events/lib/freshBuild').then((m) => m.startFreshBuildChecks());
  const MobileEventsApp = React.lazy(() => import('./mobile/events/MobileEventsApp'));
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter basename="/app">
              <React.Suspense fallback={<div className="h-screen bg-background" />}>
                <MobileEventsApp />
              </React.Suspense>
              <Toaster />
              <SonnerToaster position="bottom-center" />
            </BrowserRouter>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
} else {
// Initialize Google Analytics
initGA();

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <AppRouter />
              <Toaster />
              <SonnerToaster position="bottom-right" />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
}
